import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import RollbackHistoryPanel from '../components/RollbackHistoryPanel';
import ImportRunsPanel from '../components/ImportRunsPanel';
import { warehouseApi, brandingApi, API_BASE, docsApi } from '../api';
import { Loader2, Upload, Trash2, ImageOff, FileText, FileType2, RefreshCw, X, Calendar, CalendarOff } from 'lucide-react';

const TabButton = ({ active, onClick, children, primary, testid }) => (
  <button onClick={onClick} data-testid={testid} className={`px-4 py-1.5 rounded-md text-[13px] font-medium transition-colors ${active ? 'text-white' : 'text-[#232323] hover:bg-[#F1EBDD]'}`}
    style={active ? { background: primary } : {}}>{children}</button>
);

export default function PortalSettings() {
  const { user, theme, setTheme } = useAuth();
  const isSuper = user?.role === 'SuperAdmin';
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'theme');
  const [draft, setDraft] = useState(theme);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { setDraft(theme); }, [theme]);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      await setTheme(draft);
      setMsg('Theme saved');
      setTimeout(() => setMsg(''), 2500);
    } catch (e) { alert('Save failed'); }
    finally { setSaving(false); }
  };

  const tabs = [
    ['theme', 'Theme'],
    ['banner', 'Banner'],
    ['favicon', 'Favicon'],
    ['waybill', 'Waybill'],
    ['handbook', 'Handbook'],
    ['domain', 'Domain hosting'],
    ['smtp', 'SMTP (Gmail)'],
  ];
  const isAdmin = isSuper || user?.role === 'Admin';
  if (isAdmin) tabs.push(['import-runs', 'Import runs']);
  if (isSuper) tabs.push(['rollback', 'Rollback history']);

  return (
    <div>
      <PageHeader title="Portal settings" subtitle="Branding, hosting, and outbound mail configuration" />

      <div className="flex flex-wrap items-center gap-2 mb-5">
        {tabs.map(([key, label]) => (
          <TabButton key={key} active={tab === key} onClick={() => setTab(key)} primary={theme.primary} testid={`settings-tab-${key}`}>
            {label}
          </TabButton>
        ))}
      </div>

      {tab === 'theme' && (
        <>
          <div className="fm-card p-5 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="block text-[13px] font-medium mb-2">Primary accent color</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={draft.primary} onChange={e => setDraft(d => ({ ...d, primary: e.target.value.toUpperCase() }))} className="w-12 h-10 rounded border border-[#E7E2D3] cursor-pointer" />
                  <input value={draft.primary} onChange={e => setDraft(d => ({ ...d, primary: e.target.value }))} className="fm-focus flex-1 border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white font-mono" />
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-medium mb-2">Accent (hover / deep)</label>
                <div className="flex items-center gap-3">
                  <input type="color" value={draft.accent} onChange={e => setDraft(d => ({ ...d, accent: e.target.value.toUpperCase() }))} className="w-12 h-10 rounded border border-[#E7E2D3] cursor-pointer" />
                  <input value={draft.accent} onChange={e => setDraft(d => ({ ...d, accent: e.target.value }))} className="fm-focus flex-1 border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white font-mono" />
                </div>
              </div>
            </div>
            <div className="mt-6 flex items-center gap-3">
              <div className="text-[12px] text-[#7a6f5c]">Preview:</div>
              <button className="px-4 py-1.5 rounded-md text-white text-[13px] font-medium" style={{ background: draft.primary }}>Primary button</button>
              <button className="px-3 py-1.5 rounded-md text-[13px] font-medium" style={{ background: `${draft.primary}22`, color: draft.primary }}>Active nav item</button>
            </div>
            <p className="mt-5 text-[12.5px] text-[#7a6f5c] leading-relaxed max-w-[900px]">
              Updates primary buttons, the active sidebar item, and headline accents across the portal as soon as you save. Persists to the backend.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button disabled={saving} onClick={save} className="px-4 py-2 rounded-md text-white text-[14px] font-medium disabled:opacity-60" style={{ background: theme.primary }}>
              {saving ? <Loader2 size={14} className="animate-spin inline" /> : 'Save settings'}
            </button>
            {msg && <span className="text-[13px] text-[#2f6b41]">{msg}</span>}
          </div>
        </>
      )}

      {tab === 'banner' && (
        <LoginBannerPanel theme={theme} canEdit={user?.role === 'SuperAdmin' || user?.role === 'Admin'} />
      )}

      {tab === 'favicon' && (
        <div className="fm-card p-5">
          <div className="text-[13px] mb-3 font-medium">Favicon</div>
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-lg text-white flex items-center justify-center font-bold" style={{ background: theme.primary }}>FM</div>
            <button className="px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px]">Upload PNG (32×32)</button>
          </div>
        </div>
      )}

      {tab === 'waybill' && (
        <WaybillSettingsSection theme={theme} canEdit={user?.role === 'SuperAdmin' || user?.role === 'Admin'} />
      )}

      {tab === 'handbook' && (
        <HandbookPanel theme={theme} />
      )}

      {tab === 'domain' && (
        <div className="fm-card p-5">
          <div className="text-[13px] mb-3 font-medium">Custom domain</div>
          <input defaultValue="post.futuremakeup.com" className="fm-focus w-full max-w-[420px] border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white font-mono" />
          <p className="mt-3 text-[12px] text-[#7a6f5c]">Point an A record for <span className="font-mono">post</span> to the portal's edge IP, or map a CNAME to <span className="font-mono">portal.futuremakeup.cloud</span>.</p>
        </div>
      )}

      {tab === 'smtp' && (
        <div className="fm-card p-5">
          <p className="text-[12.5px] text-[#7a6f5c] mb-4">SMTP is wired to Gmail on the backend. Credentials live in <span className="font-mono">backend/.env</span> and cannot be edited from the browser.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <F label="SMTP host" value="smtp.gmail.com" readOnly />
            <F label="Port" value="587 (STARTTLS)" readOnly />
            <F label="From address" value="future.req.hq@gmail.com" readOnly />
            <F label="App password" value="•••• •••• •••• ••••" readOnly />
          </div>
        </div>
      )}

      {tab === 'import-runs' && isAdmin && <ImportRunsPanel theme={theme} />}
      {tab === 'rollback' && isSuper && <RollbackHistoryPanel theme={theme} />}
    </div>
  );
}

function F({ label, value, readOnly }) {
  return (
    <div>
      <div className="text-[12.5px] text-[#232323] mb-1 font-medium">{label}</div>
      <input defaultValue={value} readOnly={readOnly} className="fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-[#F7F4EA] font-mono" />
    </div>
  );
}

/**
 * SA/Admin form for the "From" address printed at the top-left of every
 * Waybill PDF. Backed by GET/PUT /api/warehouse-address. The endpoint's
 * PUT contract is address-only — it deliberately preserves the SA-uploaded
 * logo (iter24 regression) — so this form never touches image bytes.
 */
function WarehouseAddressPanel({ theme, canEdit, onDraftChange }) {
  const EMPTY = {
    companyName: '', line1: '', line2: '', city: '',
    state: '', pincode: '', phone: '', gstin: '',
  };
  const [draft, setDraft] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [dirty, setDirty] = useState(false);

  // Bubble every draft change up to the section container so the live
  // preview sees the SA's typing as they go.
  useEffect(() => { onDraftChange && onDraftChange(draft); }, [draft, onDraftChange]);

  useEffect(() => {
    (async () => {
      setLoading(true); setErr('');
      try {
        const d = await warehouseApi.get();
        // Strip API-only keys (hasLogo + logo metadata) so <input value>
        // never sees a boolean/number.
        setDraft({
          companyName: d.companyName || '',
          line1: d.line1 || '', line2: d.line2 || '',
          city: d.city || '', state: d.state || '',
          pincode: d.pincode || '', phone: d.phone || '',
          gstin: d.gstin || '',
        });
      } catch (e) {
        setErr(e.response?.data?.detail || 'Could not load warehouse address');
      } finally { setLoading(false); }
    })();
  }, []);

  const update = (patch) => {
    setDraft(d => ({ ...d, ...patch }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true); setMsg(''); setErr('');
    try {
      await warehouseApi.update(draft);
      setMsg('Warehouse address saved');
      setDirty(false);
      setTimeout(() => setMsg(''), 3000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const inputCls = 'fm-focus w-full border border-[#E7E2D3] rounded-md px-3 py-2 text-[13.5px] bg-white';

  return (
    <div className="fm-card p-5" data-testid="warehouse-address-panel">
      <div className="mb-3">
        <div className="text-[14px] font-semibold">Warehouse "FROM" address</div>
        <p className="text-[12.5px] text-[#7a6f5c] mt-1">
          Printed in the top-left <span className="font-medium">FROM</span> box of every waybill and delivery challan. All fields optional — leave blank to omit that line from the sheet.
        </p>
      </div>

      {loading ? (
        <div className="py-6 text-center text-[#7a6f5c]"><Loader2 className="inline animate-spin" size={18} /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-[12.5px] font-medium text-[#232323] mb-1">Company name</label>
              <input
                data-testid="wh-input-companyName"
                value={draft.companyName}
                onChange={e => update({ companyName: e.target.value })}
                readOnly={!canEdit}
                className={inputCls}
                placeholder="FUTURE MAKEUP INDIA PVT LTD"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[12.5px] font-medium text-[#232323] mb-1">Address line 1</label>
              <input
                data-testid="wh-input-line1"
                value={draft.line1}
                onChange={e => update({ line1: e.target.value })}
                readOnly={!canEdit}
                className={inputCls}
                placeholder="Plot / building / street"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[12.5px] font-medium text-[#232323] mb-1">Address line 2</label>
              <input
                data-testid="wh-input-line2"
                value={draft.line2}
                onChange={e => update({ line2: e.target.value })}
                readOnly={!canEdit}
                className={inputCls}
                placeholder="Area / landmark (optional)"
              />
            </div>
            <div>
              <label className="block text-[12.5px] font-medium text-[#232323] mb-1">City</label>
              <input data-testid="wh-input-city" value={draft.city} onChange={e => update({ city: e.target.value })} readOnly={!canEdit} className={inputCls} placeholder="Gurgaon" />
            </div>
            <div>
              <label className="block text-[12.5px] font-medium text-[#232323] mb-1">State</label>
              <input data-testid="wh-input-state" value={draft.state} onChange={e => update({ state: e.target.value })} readOnly={!canEdit} className={inputCls} placeholder="Haryana" />
            </div>
            <div>
              <label className="block text-[12.5px] font-medium text-[#232323] mb-1">Pincode</label>
              <input data-testid="wh-input-pincode" value={draft.pincode} onChange={e => update({ pincode: e.target.value })} readOnly={!canEdit} className={inputCls} placeholder="122001" />
            </div>
            <div>
              <label className="block text-[12.5px] font-medium text-[#232323] mb-1">Phone</label>
              <input data-testid="wh-input-phone" value={draft.phone} onChange={e => update({ phone: e.target.value })} readOnly={!canEdit} className={inputCls} placeholder="011-XXXXXXX" />
            </div>
            <div className="md:col-span-2">
              <label className="block text-[12.5px] font-medium text-[#232323] mb-1">GSTIN</label>
              <input data-testid="wh-input-gstin" value={draft.gstin} onChange={e => update({ gstin: e.target.value })} readOnly={!canEdit} className={inputCls} placeholder="06AAACR1234A1Z2" />
            </div>
          </div>

          {canEdit && (
            <div className="mt-5 flex items-center gap-3">
              <button
                data-testid="wh-save-btn"
                onClick={save}
                disabled={saving || !dirty}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-white text-[13.5px] font-medium disabled:opacity-40"
                style={{ background: theme.primary }}
                title={dirty ? 'Save changes' : 'No unsaved changes'}
              >
                {saving ? <Loader2 size={13} className="animate-spin" /> : null}
                Save address
              </button>
              {msg && <span className="text-[13px] text-[#2f6b41]" data-testid="wh-save-msg">{msg}</span>}
              {err && <span className="text-[13px] text-[#B84A4A]" data-testid="wh-save-err">{err}</span>}
            </div>
          )}
          {!canEdit && (
            <p className="mt-4 text-[12px] text-[#7a6f5c]">Only Super Admin / Admin can edit the warehouse address.</p>
          )}
        </>
      )}
    </div>
  );
}

/**
 * SA-facing widget for uploading a brand logo that swaps into every
 * Waybill PDF header (replaces the default typographic wordmark).
 *
 * UX contract:
 *   - Preview the current logo (or an empty placeholder) at the exact
 *     aspect ratio it will render inside the PDF (~16 mm tall band).
 *   - Upload button accepts PNG / JPG / WEBP up to 500 KB; validation is
 *     mirrored on the server (413 too-large / 415 wrong-mime).
 *   - Clear button drops the stored logo — waybill renders fall back to
 *     the CORE FUTURE wordmark on the next PDF.
 *   - Both actions are gated to SuperAdmin + Admin (server-side too).
 *
 * The logo GET endpoint requires a Bearer token, so we hydrate the
 * preview with `fetch` + `blob:` — never a plain `<img src>` with a
 * signed URL, since we already have the Authorization header available.
 */
function WaybillLogoPanel({ theme, canEdit, onLogoChange }) {
  const [meta, setMeta] = useState(null);          // {hasLogo, logoBytes, logoMime, logoUploadedAt, logoUploadedBy}
  const [previewUrl, setPreviewUrl] = useState(''); // blob:// or ''
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const fileRef = useRef(null);
  const objectUrlRef = useRef('');

  const loadPreview = async () => {
    // Revoke previous blob to avoid leaks.
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = '';
      setPreviewUrl('');
    }
    const token = localStorage.getItem('fm_token');
    const res = await fetch(warehouseApi.logoUrl(Date.now()), {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (res.ok) {
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      objectUrlRef.current = u;
      setPreviewUrl(u);
    }
  };

  const refresh = async () => {
    setLoading(true); setErr('');
    try {
      const m = await warehouseApi.get();
      setMeta(m);
      if (m.hasLogo) await loadPreview();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not load logo settings');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    refresh();
    // Revoke the blob URL when the component unmounts.
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bubble the logo state up so the live preview in WaybillSettingsSection
  // can compose it with the address draft.
  useEffect(() => {
    onLogoChange && onLogoChange({
      hasLogo: !!meta?.hasLogo,
      previewUrl: previewUrl || '',
    });
  }, [meta?.hasLogo, previewUrl, onLogoChange]);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const resp = await warehouseApi.uploadLogo(file);
      setMsg(`Uploaded ${Math.round(resp.sizeBytes / 1024)} KB · ${resp.logoMime}`);
      setTimeout(() => setMsg(''), 3000);
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const onClear = async () => {
    if (!window.confirm('Remove the uploaded logo? Waybills will fall back to the default CORE FUTURE wordmark.')) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await warehouseApi.clearLogo();
      setMsg('Logo removed');
      setTimeout(() => setMsg(''), 3000);
      await refresh();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Clear failed');
    } finally { setBusy(false); }
  };

  return (
    <div className="fm-card p-5" data-testid="waybill-logo-panel">
      <div className="flex items-start gap-6 flex-wrap">
        <div className="flex-1 min-w-[280px] max-w-[520px]">
          <div className="text-[14px] font-semibold mb-1">Waybill logo</div>
          <p className="text-[12.5px] text-[#7a6f5c] leading-relaxed">
            A branded PNG / JPG / WEBP appears in the top-left of every printed waybill (delivery challan) and every batch-print PDF. Recommended: a horizontal wordmark on a transparent background, ~800×240 px. Max 500 KB, max 1200×1200 px.
          </p>
          {meta?.hasLogo && meta?.logoUploadedAt && (
            <p className="text-[11.5px] text-[#7a6f5c] mt-2" data-testid="waybill-logo-meta">
              Current logo: {(meta.logoBytes ? `${Math.round(meta.logoBytes / 1024)} KB` : '')} · {meta.logoMime || ''}
              {meta.logoUploadedBy && <> · uploaded by {meta.logoUploadedBy}</>}
            </p>
          )}
        </div>

        <div className="flex flex-col items-start gap-3">
          <div
            data-testid="waybill-logo-preview"
            className="w-[260px] h-[96px] rounded-md border border-dashed border-[#E7E2D3] flex items-center justify-center bg-[#F7F4EA]"
          >
            {loading ? (
              <Loader2 size={20} className="animate-spin text-[#7a6f5c]" />
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt="Uploaded waybill logo"
                className="max-w-full max-h-full object-contain"
                data-testid="waybill-logo-preview-img"
              />
            ) : (
              <div className="flex flex-col items-center gap-1 text-[12px] text-[#7a6f5c]">
                <ImageOff size={22} />
                <span>Using default wordmark</span>
              </div>
            )}
          </div>

          {canEdit && (
            <div className="flex items-center gap-2 flex-wrap">
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                onChange={onPickFile}
                className="hidden"
                data-testid="waybill-logo-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={busy}
                data-testid="waybill-logo-upload-btn"
                className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px] font-medium disabled:opacity-50"
                style={{ background: theme.primary }}
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {meta?.hasLogo ? 'Replace logo' : 'Upload logo'}
              </button>
              {meta?.hasLogo && (
                <button
                  onClick={onClear}
                  disabled={busy}
                  data-testid="waybill-logo-clear-btn"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] font-medium hover:bg-[#F7F4EA] disabled:opacity-50"
                  style={{ color: '#B84A4A' }}
                >
                  <Trash2 size={13} /> Remove
                </button>
              )}
            </div>
          )}

          {!canEdit && (
            <p className="text-[12px] text-[#7a6f5c]">Only Super Admin / Admin can change the waybill logo.</p>
          )}

          {msg && <span className="text-[12.5px] text-[#2f6b41]" data-testid="waybill-logo-msg">{msg}</span>}
          {err && <span className="text-[12.5px] text-[#B84A4A]" data-testid="waybill-logo-err">{err}</span>}
        </div>
      </div>
    </div>
  );
}


/**
 * Container for the "Waybill" settings tab — hoists the shared draft
 * address + logo blob URL so the WYSIWYG preview at the top can compose
 * both live as the SA types / uploads. Both child panels remain
 * independent for their own save flows.
 */
function WaybillSettingsSection({ theme, canEdit }) {
  const EMPTY_ADDR = {
    companyName: '', line1: '', line2: '', city: '',
    state: '', pincode: '', phone: '', gstin: '',
  };
  const [draftAddr, setDraftAddr] = useState(EMPTY_ADDR);
  const [logoState, setLogoState] = useState({ hasLogo: false, previewUrl: '' });

  return (
    <div>
      <WaybillFromPreview
        address={draftAddr}
        logoState={logoState}
        theme={theme}
      />
      <div className="mt-6">
        <WarehouseAddressPanel
          theme={theme}
          canEdit={canEdit}
          onDraftChange={setDraftAddr}
        />
      </div>
      <div className="mt-6">
        <WaybillLogoPanel
          theme={theme}
          canEdit={canEdit}
          onLogoChange={setLogoState}
        />
      </div>
    </div>
  );
}

/**
 * WYSIWYG mock of the printed waybill's header + FROM box, updated live
 * as the SA edits the form or uploads a new logo. Kept purely visual
 * (HTML/CSS) — the ReportLab renderer stays the source of truth for the
 * actual PDF, but this preview is close enough that ops can spot the
 * obvious mistakes ("state field is blank", "logo doesn't fit", etc.)
 * without printing a real PDF every time.
 *
 * The layout mirrors `_brand_header` + `_build_address_block(FROM ...)`
 * in `/app/backend/waybill_pdf.py`:
 *   1. Left: uploaded logo image OR a typographic wordmark.
 *   2. Right: a placeholder QR square (labelled "QR · req id").
 *   3. Brand-primary 1.2 px underline separating the header from the body.
 *   4. Below: a "FROM (Warehouse)" boxed card with the address lines.
 */
function WaybillFromPreview({ address, logoState, theme }) {
  const primary = theme?.primary || '#9C3D54';
  const brandName = (address.companyName || 'FUTURE MAKEUP INDIA PVT LTD').toUpperCase();
  const cityState = [address.city, address.state, address.pincode].filter(Boolean).join(' — ');
  const lines = [
    address.companyName,
    address.line1,
    address.line2,
    cityState,
    address.phone && `Phone: ${address.phone}`,
    address.gstin && `GSTIN: ${address.gstin}`,
  ].filter(Boolean);

  return (
    <div className="fm-card p-5" data-testid="waybill-from-preview">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[14px] font-semibold">Live preview</div>
          <p className="text-[12px] text-[#7a6f5c] mt-0.5">
            How the FROM box will look on the printed waybill. Updates as you type or swap the logo.
          </p>
        </div>
        <div className="text-[11px] text-[#7a6f5c] font-mono uppercase tracking-wider">Preview only · not to scale</div>
      </div>

      {/* Simulated A4 sheet — bordered, cream background */}
      <div
        className="bg-white border border-[#E7E2D3] rounded-md p-5 max-w-[720px] mx-auto"
        data-testid="waybill-from-preview-sheet"
      >
        {/* Header strip — logo | wordmark on the left, QR placeholder on the right */}
        <div className="flex items-start justify-between gap-4 pb-2" style={{ borderBottom: `1.2px solid ${primary}` }}>
          <div className="flex-1 min-w-0">
            {logoState.hasLogo && logoState.previewUrl ? (
              <>
                <img
                  src={logoState.previewUrl}
                  alt="Waybill logo preview"
                  className="max-h-[42px] w-auto object-contain"
                  data-testid="waybill-from-preview-logo"
                />
                <div className="text-[9px] text-[#7a6f5c] mt-1">DELIVERY CHALLAN / WAYBILL — Tester units</div>
              </>
            ) : (
              <>
                <div className="text-[9px] text-[#7a6f5c]">CORE FUTURE · Tester Requisition Portal</div>
                <div
                  className="text-[17px] font-bold leading-tight"
                  style={{ color: primary }}
                  data-testid="waybill-from-preview-wordmark"
                >
                  {brandName}
                </div>
                <div className="text-[9px] text-[#7a6f5c]">DELIVERY CHALLAN / WAYBILL — Tester units</div>
              </>
            )}
          </div>
          <div className="flex flex-col items-end">
            <div className="w-[52px] h-[52px] border border-[#E7E2D3] bg-[#F7F4EA] flex items-center justify-center text-[8px] text-[#7a6f5c] text-center leading-tight">
              QR<br />req&nbsp;id
            </div>
            <div className="text-[8px] text-[#7a6f5c] mt-1">Scan → REQ-…</div>
          </div>
        </div>

        {/* FROM box card */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          <div className="bg-[#F7F4EA] border border-[#E7E2D3] rounded-md p-3">
            <div className="text-[9px] uppercase font-bold text-[#7a6f5c] tracking-wider mb-1">FROM (Warehouse)</div>
            {lines.length > 0 ? (
              <div className="text-[11px] text-[#232323] leading-[15px]" data-testid="waybill-from-preview-lines">
                {lines.map((l, i) => <div key={i}>{l}</div>)}
              </div>
            ) : (
              <div className="text-[11px] text-[#B84A4A] italic">Address is empty — the FROM box will only show a dash on the print.</div>
            )}
          </div>
          <div className="bg-[#F7F4EA] border border-dashed border-[#E7E2D3] rounded-md p-3 opacity-60">
            <div className="text-[9px] uppercase font-bold text-[#7a6f5c] tracking-wider mb-1">TO (EBO Branch)</div>
            <div className="text-[11px] text-[#7a6f5c] italic">Fills in per requisition — branch name, code, city, state, phone.</div>
          </div>
        </div>
      </div>
    </div>
  );
}


function HandbookPanel({ theme }) {
  const primary = theme?.primary || '#9C3D54';
  const pdfUrl = `${API_BASE}/docs/handbook.pdf`;
  const docxUrl = `${API_BASE}/docs/handbook.docx`;
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenMsg, setRegenMsg] = useState('');
  const [regenErr, setRegenErr] = useState('');

  const regenerate = async () => {
    if (regenBusy) return;
    setRegenBusy(true); setRegenMsg(''); setRegenErr('');
    try {
      const r = await docsApi.regenerate();
      if (r.ok) {
        const pdf = r.artifacts?.['handbook.pdf'];
        const docx = r.artifacts?.['handbook.docx'];
        setRegenMsg(`Rebuilt · PDF ${((pdf?.bytes || 0) / 1024).toFixed(0)} KB · Word ${((docx?.bytes || 0) / 1024).toFixed(0)} KB`);
      } else {
        const errs = Object.entries(r.artifacts || {})
          .filter(([, v]) => !v.ok)
          .map(([k, v]) => `${k}: ${v.error}`)
          .join(' · ');
        setRegenErr(errs || 'Rebuild failed');
      }
      setTimeout(() => { setRegenMsg(''); setRegenErr(''); }, 6000);
    } catch (e) {
      setRegenErr(e.response?.data?.detail || 'Regenerate failed');
      setTimeout(() => setRegenErr(''), 6000);
    } finally {
      setRegenBusy(false);
    }
  };

  const DownloadCard = ({ href, testid, icon: Icon, label, sub, sizeHint }) => (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      data-testid={testid}
      className="flex items-center gap-4 p-4 rounded-lg border border-[#E7E2D3] bg-white hover:bg-[#FBFAF7] hover:border-[#D9D1BB] transition-colors group"
    >
      <div
        className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0 text-white group-hover:scale-105 transition-transform"
        style={{ background: primary }}
      >
        <Icon size={20} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[14px] font-medium text-[#232323]">{label}</div>
        <div className="text-[12px] text-[#7a6f5c] mt-0.5">{sub}</div>
      </div>
      <div className="text-[11px] text-[#7a6f5c] font-mono shrink-0">{sizeHint}</div>
    </a>
  );

  return (
    <div className="fm-card p-6">
      <div className="mb-5">
        <h3 className="text-[16px] font-medium text-[#232323] mb-1">Portal handbook</h3>
        <p className="text-[12.5px] text-[#7a6f5c] leading-relaxed max-w-[720px]">
          The complete handbook covering the Scope of Work, step-by-step user guide for every role
          (Super Admin, Admin, EBO, Warehouse, Logistics, Zone Manager, All-India Manager,
          Retail Manager), and the developer onboarding guide. Share these files with vendors,
          new team members, and auditors — no portal login required to open them.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-[820px]">
        <DownloadCard
          href={pdfUrl}
          testid="handbook-download-pdf-btn"
          icon={FileText}
          label="Download PDF"
          sub="Opens on any tablet, phone, or browser. No Word needed."
          sizeHint="~86 KB"
        />
        <DownloadCard
          href={docxUrl}
          testid="handbook-download-docx-btn"
          icon={FileType2}
          label="Download Word (.docx)"
          sub="Editable version — send to a vendor to mark up sections."
          sizeHint="~68 KB"
        />
      </div>

      <div className="mt-5 pt-4 border-t border-[#EFEAD9]">
        <div className="flex items-center gap-3 flex-wrap">
          <button
            onClick={regenerate}
            disabled={regenBusy}
            data-testid="handbook-regenerate-btn"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA] disabled:opacity-60"
            style={{ color: primary }}
          >
            {regenBusy ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Regenerate handbook
          </button>
          {regenMsg && <span className="text-[12.5px] text-[#2f6b41]" data-testid="handbook-regenerate-ok">{regenMsg}</span>}
          {regenErr && <span className="text-[12.5px] text-[#a04747]" data-testid="handbook-regenerate-err">{regenErr}</span>}
        </div>
        <div className="mt-3 text-[11.5px] text-[#7a6f5c] leading-relaxed max-w-[720px]">
          <span className="font-medium text-[#232323]">Contents:</span>{' '}
          Part I — Scope of Work · Part II — User Guide (per role, step-by-step) · Part III — Developer Handbook (setup, testing, architecture, deployment).
          Click <span className="italic">Regenerate handbook</span> after editing the source markdown to rebuild both files in place.
        </div>
      </div>
    </div>
  );
}


/** iter43 — Login-banner uploader. Mirrors WaybillLogoPanel: fetch status via
 *  /api/branding/status, preview the current bytes via <img>, POST upload,
 *  DELETE clear. SA/Admin only. */
function LoginBannerPanel({ theme, canEdit }) {
  const [banners, setBanners] = useState([]);
  const [maxCount, setMaxCount] = useState(5);
  const [activeIndex, setActiveIndex] = useState(0);
  const [todayIST, setTodayIST] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');
  const [bust, setBust] = useState(Date.now());
  // iter44 — Preview a locally-selected file BEFORE upload so SA sees the
  // exact 16:9 crop that the Login page will render (object-cover, centred).
  const [localPreview, setLocalPreview] = useState(null); // { file, url }
  // iter46 — Schedule editor state. `editing` holds the banner being pinned.
  const [editing, setEditing] = useState(null); // { id, start, end }
  const fileRef = useRef();

  const hasBanner = banners.length > 0;
  const galleryFull = banners.length >= maxCount;

  const refresh = () => brandingApi.status().then(r => {
    setBanners(r?.banners || []);
    setMaxCount(r?.maxCount || 5);
    setActiveIndex(Number.isInteger(r?.activeIndex) ? r.activeIndex : 0);
    setTodayIST(r?.todayIST || '');
  }).catch(() => setBanners([]));

  useEffect(() => { refresh(); }, []);

  // Revoke the object URL when the local preview changes / component unmounts
  // to avoid leaking DOM refs to the picked file.
  useEffect(() => {
    return () => { if (localPreview?.url) URL.revokeObjectURL(localPreview.url); };
  }, [localPreview]);

  const onFilePicked = (file) => {
    if (!file) return;
    // Show a 16:9 preview of the selected file BEFORE we send it to the server
    // so the SA can decide whether to keep or replace. Old preview URL is
    // revoked in the cleanup effect above.
    setLocalPreview({ file, url: URL.createObjectURL(file) });
    setMsg(''); setErr('');
  };

  const commitUpload = async () => {
    if (!localPreview?.file) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await brandingApi.uploadBanner(localPreview.file);
      await refresh();
      setBust(Date.now());
      setLocalPreview(null);
      setMsg('Login banner added to the rotation gallery.');
      setTimeout(() => setMsg(''), 5000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Upload failed');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const cancelPreview = () => {
    setLocalPreview(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const removeOne = async (bannerId) => {
    if (!window.confirm('Remove this banner from the daily rotation?')) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await brandingApi.removeBanner(bannerId);
      await refresh();
      setBust(Date.now());
      setMsg('Banner removed from rotation.');
      setTimeout(() => setMsg(''), 5000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Remove failed');
    } finally { setBusy(false); }
  };

  const clearAll = async () => {
    if (!window.confirm('Remove ALL banners and revert to the default monogram?')) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await brandingApi.clearBanner();
      await refresh();
      setBust(Date.now());
      setMsg('All banners cleared — default monogram will show on next sign-in.');
      setTimeout(() => setMsg(''), 5000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Clear failed');
    } finally { setBusy(false); }
  };

  // iter46 — Banner Scheduler handlers -----------------------------------
  const openScheduler = (banner) => {
    setEditing({
      id: banner.id,
      start: banner.scheduleStart || todayIST || '',
      end: banner.scheduleEnd || todayIST || '',
    });
    setMsg(''); setErr('');
  };

  const saveSchedule = async () => {
    if (!editing) return;
    if (!editing.start || !editing.end) {
      setErr('Both start and end dates are required'); return;
    }
    if (editing.end < editing.start) {
      setErr('End date must be on or after start date'); return;
    }
    setBusy(true); setErr(''); setMsg('');
    try {
      await brandingApi.setSchedule(editing.id, editing.start, editing.end);
      await refresh();
      setBust(Date.now());
      setEditing(null);
      setMsg('Schedule pinned — this banner will take over during that window.');
      setTimeout(() => setMsg(''), 5000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not save schedule');
    } finally { setBusy(false); }
  };

  const clearScheduleOne = async (bannerId) => {
    if (!window.confirm('Unpin this banner? It will return to the daily rotation pool.')) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      await brandingApi.clearSchedule(bannerId);
      await refresh();
      setBust(Date.now());
      setMsg('Schedule cleared — banner is back in the rotation.');
      setTimeout(() => setMsg(''), 5000);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Could not clear schedule');
    } finally { setBusy(false); }
  };

  const fmtRange = (b) => {
    if (!b.scheduleStart || !b.scheduleEnd) return null;
    // Display as `DD MMM` — compact, no year for common case.
    const fmt = (iso) => {
      try {
        const [y, m, d] = iso.split('-').map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' });
      } catch { return iso; }
    };
    return b.scheduleStart === b.scheduleEnd
      ? fmt(b.scheduleStart)
      : `${fmt(b.scheduleStart)} – ${fmt(b.scheduleEnd)}`;
  };

  return (
    <div className="fm-card p-5" data-testid="branding-login-banner-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="text-[15px] font-semibold text-[#232323]">Login page banners</div>
        {hasBanner && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#E7F2E9] text-[#2f6b41] border border-[#B4D7B8]" data-testid="branding-banner-count">
            {banners.length} of {maxCount} · rotates daily
          </span>
        )}
      </div>
      <p className="text-[12px] text-[#7a6f5c] mb-4 max-w-[640px]">
        Upload up to <b>{maxCount}</b> hero images (PNG / JPEG / WEBP, ≤500 KB, up to 2400×1600 px). By default the Login page rotates through them once per day (IST). Pin any banner to a <b>specific date window</b> (e.g. Diwali week) and it will take over automatically — pinned banners win over daily rotation, and if two pinned banners overlap, the most recently uploaded wins.
      </p>

      {/* Preview / new-upload frame */}
      <div className="border border-[#E7E2D3] rounded-md overflow-hidden bg-[#F7F4EA] mb-3 relative" style={{ width: 480, maxWidth: '100%', aspectRatio: '16/9' }} data-testid="branding-banner-frame">
        {localPreview?.url ? (
          <>
            <img src={localPreview.url} alt="Selected banner preview" data-testid="branding-local-preview" className="w-full h-full object-cover" />
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)' }} />
            <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
              <div className="absolute inset-y-0 left-1/3 border-l border-white/25"></div>
              <div className="absolute inset-y-0 left-2/3 border-l border-white/25"></div>
              <div className="absolute inset-x-0 top-1/3 border-t border-white/25"></div>
              <div className="absolute inset-x-0 top-2/3 border-t border-white/25"></div>
            </div>
            <div className="absolute top-2 left-2 text-[10.5px] font-mono px-1.5 py-0.5 rounded bg-black/55 text-white pointer-events-none">
              16 : 9 · object-cover
            </div>
            <div className="absolute top-2 right-2 text-[10.5px] font-medium px-1.5 py-0.5 rounded bg-[#B78140] text-white pointer-events-none" data-testid="branding-preview-badge">
              Preview · not yet saved
            </div>
          </>
        ) : hasBanner ? (
          // Show today's active slot (server-picked, respects Banner Scheduler).
          (() => {
            const idx = Math.max(0, Math.min(banners.length - 1, activeIndex));
            const active = banners[idx];
            const pinned = !!(active?.scheduleStart && active?.scheduleEnd);
            return (
              <>
                <img
                  key={`${active?.id}-${bust}`}
                  src={brandingApi.bannerUrl(bust, idx)}
                  alt="Today's login banner"
                  data-testid="branding-current-banner"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)' }} />
                <div className="absolute top-2 left-2 text-[10.5px] font-mono px-1.5 py-0.5 rounded bg-black/55 text-white pointer-events-none">
                  Slot {idx + 1} of {banners.length} · today
                </div>
                {pinned && (
                  <div className="absolute top-2 right-2 text-[10.5px] font-medium px-1.5 py-0.5 rounded bg-[#9C3D54] text-white pointer-events-none flex items-center gap-1" data-testid="branding-current-pinned">
                    <Calendar size={11} /> Pinned · {fmtRange(active)}
                  </div>
                )}
              </>
            );
          })()
        ) : (
          <div className="w-full h-full flex items-center justify-center text-[#7a6f5c] text-[13px]" data-testid="branding-no-banner">
            <ImageOff size={16} className="mr-2" /> Using default monogram
          </div>
        )}
      </div>

      <div className="text-[11.5px] text-[#7a6f5c] mb-4 max-w-[480px] leading-relaxed">
        <b>16 : 9 aspect ratio</b> — the Login page uses <span className="font-mono">object-cover</span>, so anything outside the frame above will be trimmed. Keep the focal point away from the bottom edge and use the rule-of-thirds guides to align it.
      </div>

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2 mb-4">
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={e => onFilePicked(e.target.files?.[0])} className="hidden" data-testid="branding-banner-file" />
          {!localPreview && (
            <>
              <button onClick={() => fileRef.current?.click()} disabled={busy || galleryFull}
                      data-testid="branding-banner-upload"
                      title={galleryFull ? `Gallery is full (${maxCount}). Remove one to add another.` : ''}
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px] disabled:opacity-60"
                      style={{ background: theme.primary }}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                {hasBanner ? 'Add another banner' : 'Choose banner'}
              </button>
              {hasBanner && (
                <button onClick={clearAll} disabled={busy}
                        data-testid="branding-banner-clear"
                        className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]">
                  <Trash2 size={13} /> Clear all
                </button>
              )}
              {galleryFull && (
                <span className="text-[11.5px] text-[#B78140]" data-testid="branding-full-hint">
                  Gallery is full. Remove one below to upload another.
                </span>
              )}
            </>
          )}
          {localPreview && (
            <>
              <button onClick={commitUpload} disabled={busy}
                      data-testid="branding-banner-commit"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-white text-[13px] disabled:opacity-60"
                      style={{ background: theme.primary }}>
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                Save this banner
              </button>
              <button onClick={cancelPreview} disabled={busy}
                      data-testid="branding-banner-cancel"
                      className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]">
                <X size={13} /> Cancel
              </button>
              <span className="text-[11.5px] text-[#7a6f5c] ml-2">
                {localPreview.file?.name} · {Math.round((localPreview.file?.size || 0) / 1024)} KB
              </span>
            </>
          )}
        </div>
      ) : (
        <p className="text-[12px] text-[#7a6f5c]">Only Super Admin / Admin can upload or clear banners.</p>
      )}

      {/* iter45 — Gallery thumbnails so SAs can see every uploaded banner and remove any one. */}
      {/* iter46 — Also shows schedule badge + pin / unpin actions per banner. */}
      {hasBanner && (
        <div data-testid="branding-banner-gallery">
          <div className="text-[11.5px] uppercase tracking-wide text-[#7a6f5c] mb-2 font-medium">Rotation gallery</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {banners.map((b, i) => {
              const pinned = !!(b.scheduleStart && b.scheduleEnd);
              const isActive = i === activeIndex;
              return (
                <div key={b.id} className={`relative border rounded-md overflow-hidden bg-[#F7F4EA] ${isActive ? 'border-[#9C3D54] ring-2 ring-[#9C3D54]/30' : 'border-[#E7E2D3]'}`} style={{ aspectRatio: '16/9' }} data-testid={`branding-banner-thumb-${i}`}>
                  <img
                    src={brandingApi.bannerUrl(bust, i)}
                    alt={`Banner ${i + 1}`}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute top-1 left-1 text-[10px] font-mono px-1 py-0.5 rounded bg-black/55 text-white">
                    Slot {i + 1}{isActive ? ' · today' : ''}
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => removeOne(b.id)}
                      disabled={busy}
                      data-testid={`branding-banner-thumb-remove-${i}`}
                      title="Remove this banner from the rotation"
                      className="absolute top-1 right-1 p-1 rounded bg-black/55 text-white hover:bg-[#B84A4A]"
                    >
                      <Trash2 size={11} />
                    </button>
                  )}
                  {/* Bottom schedule strip */}
                  <div className="absolute bottom-0 inset-x-0 bg-black/60 text-white text-[10.5px] px-1.5 py-1 flex items-center justify-between gap-1">
                    {pinned ? (
                      <span className="flex items-center gap-1 truncate" data-testid={`branding-banner-schedule-${i}`}>
                        <Calendar size={10} /> {fmtRange(b)}
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-white/70" data-testid={`branding-banner-schedule-${i}`}>
                        <RefreshCw size={10} /> Daily rotation
                      </span>
                    )}
                    {canEdit && (
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => openScheduler(b)}
                          disabled={busy}
                          data-testid={`branding-banner-schedule-edit-${i}`}
                          title={pinned ? 'Edit schedule window' : 'Pin to a date window'}
                          className="p-1 rounded hover:bg-white/20"
                        >
                          <Calendar size={11} />
                        </button>
                        {pinned && (
                          <button
                            onClick={() => clearScheduleOne(b.id)}
                            disabled={busy}
                            data-testid={`branding-banner-schedule-clear-${i}`}
                            title="Unpin — return to daily rotation"
                            className="p-1 rounded hover:bg-white/20"
                          >
                            <CalendarOff size={11} />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* iter46 — Schedule editor modal */}
      {editing && canEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="branding-schedule-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-[420px] p-5 border border-[#E7E2D3]">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[15px] font-semibold text-[#232323] flex items-center gap-2">
                <Calendar size={16} /> Pin banner to a date window
              </div>
              <button onClick={() => setEditing(null)} data-testid="branding-schedule-modal-close" className="p-1 rounded hover:bg-[#F1EBDD]">
                <X size={16} />
              </button>
            </div>
            <p className="text-[12px] text-[#7a6f5c] mb-4 leading-relaxed">
              Pinned banners take over daily rotation during their window (inclusive, IST). If two pinned banners overlap the same day, the <b>most recently uploaded</b> one wins.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <label className="text-[12px] text-[#232323]">
                Start date
                <input
                  type="date"
                  value={editing.start}
                  onChange={e => setEditing({ ...editing, start: e.target.value })}
                  data-testid="branding-schedule-start"
                  className="mt-1 w-full px-2 py-1.5 rounded border border-[#E7E2D3] bg-white text-[13px]"
                />
              </label>
              <label className="text-[12px] text-[#232323]">
                End date
                <input
                  type="date"
                  value={editing.end}
                  min={editing.start || undefined}
                  onChange={e => setEditing({ ...editing, end: e.target.value })}
                  data-testid="branding-schedule-end"
                  className="mt-1 w-full px-2 py-1.5 rounded border border-[#E7E2D3] bg-white text-[13px]"
                />
              </label>
            </div>
            {err && <div className="mb-3 text-[12px] px-3 py-2 rounded bg-[#FBEFEF] text-[#B84A4A]" data-testid="branding-schedule-err">{err}</div>}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setEditing(null)}
                disabled={busy}
                data-testid="branding-schedule-cancel"
                className="px-3 py-1.5 rounded border border-[#E7E2D3] bg-white text-[13px] hover:bg-[#F7F4EA]"
              >
                Cancel
              </button>
              <button
                onClick={saveSchedule}
                disabled={busy}
                data-testid="branding-schedule-save"
                className="px-3 py-1.5 rounded text-white text-[13px] disabled:opacity-60 inline-flex items-center gap-2"
                style={{ background: theme.primary }}
              >
                {busy ? <Loader2 size={13} className="animate-spin" /> : <Calendar size={13} />}
                Save schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {msg && <div className="mt-3 text-[12.5px] px-3 py-2 rounded bg-[#E7F2E9] text-[#2f6b41]">{msg}</div>}
      {err && !editing && <div className="mt-3 text-[12.5px] px-3 py-2 rounded bg-[#FBEFEF] text-[#B84A4A]">{err}</div>}
    </div>
  );
}
